import { CategoryLabel, type CategoryLabelInput } from './categoryLabel';

const inputs: CategoryLabelInput[] = [
    { key: 'acknowledgment', name: 'Acknowledgment', color: '#FF8C00', publicationDate: false },
    { key: 'article', name: 'Article', color : '#5B8E7D', publicationDate: false },
    { key: 'best-practice', name: 'Best Practice', color: '#FFD138', publicationDate: false },
    { key: 'big-story', name: 'Big Story', color: '#02D34A', publicationDate: false },
    { key: 'blog', name: 'Blog', color: '#8D99A5', publicationDate: false },
    { key: 'brochure', name: 'Brochure', color: '#00E0CE', publicationDate: false },
    { key: 'case-study', name: 'Case Study', color: '#EB0037', publicationDate: false },
    { key: 'company-website', name: 'Company  Website', publicationDate: false },
    { key: 'product-website', name: 'Product  Website', publicationDate: false },
    { key: 'event', name: 'Event', publicationDate: false },
    { key: 'financial-news', name: 'Financial News', color: '#0B5A55', publicationDate: true },
    { key: 'labs', name: 'Labs', color: '#D314C0', publicationDate: false },
    { key: 'news', name: 'News', color: '#FF8C00', publicationDate: true },
    { key: 'offering', name: 'Offering', publicationDate: false },
    { key: 'podcast', name: 'Podcast', color: '#0052CC', publicationDate: false },
    { key: 'press', name: 'Press', color: '#FF8C00', publicationDate: true },
    { key: 'product-and-service', name: 'Product & Accelerator', color: '#02D34A', publicationDate: false },
    { key: 'research', name: 'Research', color: '#00E0CE', publicationDate: false },
    { key: 'rose', name: 'ROSE', color: '#E50D6A', publicationDate: false },
    { key: 'webinar', name: 'Webinar', publicationDate: false },
    { key: 'trend-sonar-reports', name: 'Trend Sonar Reports', displayName: 'Research', color: '#00E0CE', publicationDate: false },
    { key: 'white-paper', name: 'White Paper', color: '#00E0CE', publicationDate: false },
    { key: 'partnership', name: 'Partnership', color: '#B9AADD', publicationDate: false },
].sort((prev: CategoryLabelInput, next: CategoryLabelInput): number => prev.name.localeCompare(next.name));

class CategoryLabelsFactory {

    private readonly categoryLabels: CategoryLabel[];

    private constructor() {
        this.categoryLabels = inputs.map((i: CategoryLabelInput) => new CategoryLabel(i));
    }

    public static build(): CategoryLabelsFactory {
        return new CategoryLabelsFactory();
    }

    public get(key?: string): CategoryLabel[] {
        if (!key) {
            return JSON.parse(JSON.stringify(this.categoryLabels));
        }
        return JSON.parse(JSON.stringify(this.categoryLabels.filter((i: CategoryLabel) => i.key === key)));
    }

    public getWithEmptyOption(name?: string): CategoryLabel[] {
        const labels: CategoryLabel[] = this.get();
        labels.unshift(this.getEmpty(name));
        return labels;
    }

    public getWithInheritOption(name?: string): CategoryLabel[] {
        const labels: CategoryLabel[] = this.get();
        labels.unshift(this.getInherit(name));
        return labels;
    }

    public getWithEmptyAndInheritOption(name?: string): CategoryLabel[] {
        const labels: CategoryLabel[] = this.get();
        labels.unshift(this.getEmpty(name));
        labels.unshift(this.getInherit(name));
        return labels;
    }

    public getDefault(): CategoryLabel {
        return JSON.parse(JSON.stringify(this.categoryLabels.find((cl: CategoryLabel): boolean => !!cl.color)));
    }

    public getDefaultKey(): string {
        return this.categoryLabels.find((cl: CategoryLabel): boolean => !!cl.color).key;
    }

    public getEmpty(name: string = '-- None --'): CategoryLabel {
        return new CategoryLabel({ key: 'none', name: name, publicationDate: false });
    }

    public getEmptyKey(): string {
        return this.getEmpty().key;
    }

    public getInherit(name: string = '-- Inherit --'): CategoryLabel {
        return new CategoryLabel({ key: 'inherit', name: name, publicationDate: true });
    }

    public getInheritKey(): string {
        return this.getInherit().key;
    }

    public getDummy(name: string = 'Category label'): CategoryLabel {
        return new CategoryLabel({ key: 'dummy', name: name, color: '#151F27', publicationDate: false });
    }

    public getDummyKey(): string {
        return this.getDummy().key;
    }

    public having(input: { color?: string | boolean, publicationDate?: boolean }): CategoryLabel[] {
        if (!input) {
            return [];
        }
        const filterByColor: boolean = typeof input.color !== 'undefined';
        const filterByPublicationDate: boolean = typeof input.publicationDate !== 'undefined';
        if (filterByColor || filterByPublicationDate) {
            return JSON.parse(JSON.stringify(this.categoryLabels.filter(
                (categoryLabel: CategoryLabel): boolean => {
                    if (filterByColor && filterByPublicationDate) {
                        if (typeof input.color === 'string') {
                            return categoryLabel.color === input.color && categoryLabel.publicationDate === input.publicationDate;
                        } else {
                            return !!categoryLabel.color === input.color && input.publicationDate;
                        }
                    } else if (filterByColor) {
                        if (typeof input.color === 'string') {
                            return categoryLabel.color === input.color;
                        } else {
                            return !!categoryLabel.color === input.color;
                        }
                    } else if (filterByPublicationDate) {
                        return categoryLabel.publicationDate === input.publicationDate;
                    }
                }
            )));
        }
        return [];
    }
}

const categoryLabels = CategoryLabelsFactory.build();

export { categoryLabels as CategoryLabels };